// This is not strictly a React component (but modelled to look similar).
// We need to import React though as it's needed to create nodes with embedded elements (for the feature label)
import React, { Fragment } from 'react';

import PropTypes from 'prop-types';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-gl-extras.css';
import drawStyles from './draw-styles';
import DrawCircle from './DrawCircle';
import DrawCircleNew from './DrawCircleNew';
import ExtendedSimpleSelect from './ExtendedSimpleSelect';
import ExtendedDirectSelect from './ExtendedDirectSelect';
import ExtendedDrawLineString from './ExtendedDrawLineString';
import ExtendedDrawPolygon from './ExtendedDrawPolygon';
import LockedDirectSelect from './LockedDirectSelect';

import CircleMode from './NewDraw/CircleMode';
import DirectModeOverride from './NewDraw/DirectModeOverride';
import SimpleSelectModeOverride from './NewDraw/SimpleSelectModeOverride';
import DragCircleMode from './NewDraw/DragCircleMode';

import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

class Draw {
    static propTypes = {
        map: PropTypes.object.isRequired,
        drawControls: PropTypes.object,
        updateDrawnFeature: PropTypes.func.isRequired,
        onDrawModeChange: PropTypes.func.isRequired
    };

    constructor(props) {
        this.props = props;

        this.drawControl = new MapboxDraw({
            boxSelect: true,
            displayControlsDefault: false,
            // controls: this.props.drawControls,
            keybindings: false,
            modes: {
                ...MapboxDraw.modes,
                // draw_circle: DrawCircle,
                draw_circle: DrawCircleNew,
                // simple_select: ExtendedSimpleSelect,
                // direct_select: ExtendedDirectSelect,
                locked_direct_select: LockedDirectSelect,
                extrude_mode: ExtendedSimpleSelect,
                draw_line_string: ExtendedDrawLineString,
                draw_polygon: ExtendedDrawPolygon,
                // from library
                // draw_circle: CircleMode,
                drag_circle: DragCircleMode,
                direct_select: DirectModeOverride,
                simple_select: SimpleSelectModeOverride
            },
            userProperties: true,
            styles: this.props.drawStyles ? this.props.drawStyles : drawStyles
        });
        this.drawControl.modes.DRAW_CIRCLE = 'draw_circle';
        this.drawControl.types.CIRCLE = 'circle';

        this.drawControl.modes.EXTRUDE_MODE = 'extrude_mode';
        this.drawControl.types.EXTRUDE = 'extrude';

        this.drawControl.types.PROC_MODEL = 'proc_model';

        this.drawControl.modes.LOCKED_DIRECT_SELECT = 'locked_direct_select';

        // Add event listeners manually for keydown press. These should be inline in the mapbox-gl-draw/src/setup connect and onremove functions
        // We assume that the draw control is always loaded after the map has loaded so the map prop should always be available
        const onAdd = this.drawControl.onAdd;
        this.drawControl.onAdd = map => {
            const controlContainer = onAdd(map);
            const container = props.map.getContainer();
            container.addEventListener('keydown', this.handleKeyDown);
            return controlContainer;
        };

        const onRemove = this.drawControl.onRemove;
        this.drawControl.onRemove = () => {
            const container = props.map.getContainer();
            container.removeEventListener('keydown', this.handleKeyDown);
            return onRemove();
        };

        //
        this.props.map.on('draw.create', this.handleDrawCreate);
        this.props.map.on('draw.delete', this.handleDrawDelete);
        this.props.map.on('draw.update', this.handleDrawSelectionUpdate);
        this.props.map.on('draw.selectionchange', this.handleDrawSelectionChange);

        this.props.map.on('draw.modechange', this.handleDrawModeChange);

        this.props.map.on('draw.editing', this.handleDrawEditing);
        this.props.map.on('draw.cancelled', this.handleDrawCancelled);
    }

    // An identical check inside mapbox-gl-draw/src/setup for keydown events exists however it seems to not work for some reason.
    // By using a custom handler we can ensure the delete shortcut works as it is supposed to.
    handleKeyDown = event => {
        if ((event.srcElement || event.target).classList[0] !== 'mapboxgl-canvas') return;

        if (event.keyCode === 8 || event.keyCode === 46) {
            this.drawControl.trash();
        }
    };

    getDrawControl = () => {
        return this.drawControl;
    };

    getMode = () => {
        return this.drawControl.getMode();
    };

    changeMode = (mode, modeOptions = {}) => {
        if (this.drawControl.getMode() === mode) {
            mode = this.drawControl.modes.SIMPLE_SELECT;
        }
        this.drawControl.changeMode(mode, modeOptions);
        this.handleDrawModeChange({ mode });
    };

    setProcModel = () => {
        let collection = this.drawControl.getSelected();
        for (let feature of collection.features) {
            if (feature.geometry.type === 'Polygon') {
                this.drawControl.setFeatureProperty(feature.id, 'proc_model', {});
                this.props.updateDrawnFeature(feature);
            }
        }
    };

    handleDrawModeChange = ({ mode }) => {
        const { onDrawModeChange } = this.props;
        onDrawModeChange(mode);
    };

    handleDrawCreate = params => {};

    handleDrawDelete = params => {};

    handleDrawSelectionChange = params => {};

    handleDrawSelectionUpdate = params => {};

    handleDrawEditing = params => {};

    handleDrawCancelled = () => {};
}

export default Draw;
